import { toLowerCase } from 'Utils/string';

type ComponentErrorAction = 'warn' | 'error' | 'drop';

type ComponentErrorLike = { name?: string; message?: string; stack?: string };

type ComponentErrorSignature = {
  errorName?: string;
  messageIncludes?: string[];
  stackIncludes?: string[];
  componentStackIncludes?: string[];
};

type ComponentErrorRule = {
  id: string;
  componentNames?: string[];
  action: ComponentErrorAction;
  when?: {
    translatedEnvironmentDetected?: boolean;
  };
  signatures: ComponentErrorSignature[];
};

export const defaultComponentErrorPolicy = Object.freeze({
  rules: [
    {
      id: 'external_dom_remove_child_not_found',
      action: 'warn',
      when: { translatedEnvironmentDetected: true },
      signatures: [
        {
          errorName: 'NotFoundError',
          messageIncludes: ["Failed to execute 'removeChild' on 'Node'", 'not a child of this node'],
        },
        {
          errorName: 'NotFoundError',
          messageIncludes: ['The object can not be found here.'],
          stackIncludes: ['removeChild@[native code]'],
        },
      ],
    },
  ],
}) satisfies { rules: readonly ComponentErrorRule[] };

export function getComponentErrorHandlingDecision({
  componentName,
  error,
  componentStack,
  translatedEnvironmentDetected = false,
  policy = defaultComponentErrorPolicy,
}: {
  componentName?: string;
  error?: ComponentErrorLike | null;
  componentStack?: string;
  translatedEnvironmentDetected?: boolean;
  policy?: typeof defaultComponentErrorPolicy;
} = {}): { action: ComponentErrorAction; reason: string | null; matchedRuleId: string | null } {
  const matchedRule = policy.rules.find((rule) =>
    doesComponentErrorMatchRule({
      componentName,
      error,
      componentStack,
      translatedEnvironmentDetected,
      rule,
    }),
  );

  return matchedRule
    ? { action: matchedRule.action, matchedRuleId: matchedRule.id, reason: matchedRule.id }
    : { action: 'error', matchedRuleId: null, reason: null };
}

function doesComponentErrorMatchRule({
  componentName,
  error,
  componentStack,
  translatedEnvironmentDetected,
  rule,
}: {
  componentName?: string;
  error?: ComponentErrorLike | null;
  componentStack?: string;
  translatedEnvironmentDetected: boolean;
  rule: ComponentErrorRule;
}): boolean {
  if (
    rule.when?.translatedEnvironmentDetected !== undefined &&
    rule.when.translatedEnvironmentDetected !== translatedEnvironmentDetected
  ) {
    return false;
  }

  if (rule.componentNames && !rule.componentNames.includes(componentName ?? '')) {
    return false;
  }

  return rule.signatures.some((signature) => matchesSignature({ error, componentStack, signature }));
}

function matchesSignature({
  error,
  componentStack,
  signature,
}: {
  error?: ComponentErrorLike | null;
  componentStack?: string;
  signature: ComponentErrorSignature;
}): boolean {
  return (
    (!signature.errorName || signature.errorName === error?.name) &&
    includesAll(error?.message, signature.messageIncludes) &&
    includesAll(error?.stack, signature.stackIncludes) &&
    includesAll(componentStack, signature.componentStackIncludes)
  );
}

function includesAll(value: unknown, parts: string[] = []): boolean {
  const normalizedValue = toLowerCase(value);
  return parts.every((part) => normalizedValue.includes(toLowerCase(part)));
}
